import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ExternalLink, Building2, Search } from 'lucide-react';
import { DIRECTORY_CATEGORIES } from './DirectorySubmitPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LogoImg = ({ src, alt }) => {
  const [errored, setErrored] = useState(false);
  if (!src || errored) {
    return <Building2 className="w-6 h-6 text-slate-400" />;
  }
  return (
    <img
      src={src}
      alt={alt}
      className="w-full h-full object-contain"
      onError={() => setErrored(true)}
    />
  );
};

const DISCLAIMER =
  'All listings are reviewed by Safe Standard before publication. The Safe Standard Directory is an informational resource designed to help map the internet and AI safety ecosystem. Inclusion in the directory does not constitute endorsement, certification, approval, partnership, or formal verification by Safe Standard unless expressly stated. Safe Standard may review, edit, decline, archive, or remove listings at its discretion.';

const DirectoryPage = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchListings = async () => {
      try {
        const res = await axios.get(`${API}/directory/listings`);
        setListings(res.data?.listings || []);
      } catch (err) {
        setListings([]);
      } finally {
        setLoading(false);
      }
    };
    fetchListings();
  }, []);

  const filtered = listings.filter((l) => {
    const matchCat = activeCategory === 'All' || l.category === activeCategory;
    const q = search.trim().toLowerCase();
    const matchSearch =
      !q ||
      l.org_name?.toLowerCase().includes(q) ||
      l.description?.toLowerCase().includes(q) ||
      l.country?.toLowerCase().includes(q) ||
      l.region?.toLowerCase().includes(q);
    return matchCat && matchSearch;
  });

  const categories = ['All', ...DIRECTORY_CATEGORIES];

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="directory-page">
      <section className="bg-slate-900 text-white py-16 md:py-24">
        <div className="max-w-5xl mx-auto container-padding">
          <p className="caption text-cyan-400 mb-4">Safe Standard Directory</p>
          <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tight text-white mb-6">
            Mapping the internet &amp; AI safety ecosystem.
          </h1>
          <p className="text-base md:text-lg text-slate-300 max-w-3xl">
            An informational directory of the organizations, research labs, regulators, tools,
            and community groups working on internet and AI safety. Every listing is reviewed
            by Safe Standard before publication.
          </p>
          <div className="mt-8">
            <Link to="/directory/submit" className="btn-primary inline-block" data-testid="directory-submit-cta">
              Submit a listing
            </Link>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto container-padding">
          {/* Search + category filters */}
          <div className="mb-8 space-y-5">
            <div className="relative max-w-md">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search organizations…"
                className="w-full pl-10 pr-4 py-3 border border-slate-300 text-sm focus:outline-none focus:border-slate-900 bg-white"
                data-testid="directory-search"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 text-xs font-medium uppercase tracking-wider border transition-colors ${
                    activeCategory === cat
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-slate-600 border-slate-300 hover:border-slate-900'
                  }`}
                  data-testid={`directory-filter-${cat.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Listings grid */}
          {loading ? (
            <p className="text-slate-500 text-center py-16">Loading directory…</p>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16" data-testid="directory-empty">
              <p className="text-slate-500 mb-4">No listings to display yet.</p>
              <Link to="/directory/submit" className="text-cyan-700 hover:underline text-sm font-medium">
                Be the first to submit a listing →
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="directory-grid">
              {filtered.map((l) => (
                <div key={l.id} className="bg-white border border-slate-200 p-6 flex flex-col hover:border-slate-400 transition-colors" data-testid={`directory-card-${l.id}`}>
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-14 h-14 bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      <LogoImg src={l.logo_url} alt={l.org_name} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-base font-bold text-slate-900 leading-tight">{l.org_name}</h3>
                      <span className="inline-block mt-1.5 text-[10px] font-semibold uppercase tracking-wider text-cyan-700 bg-cyan-50 px-2 py-0.5">
                        {l.category}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed flex-1">{l.description}</p>
                  {(l.region || l.country) && (
                    <p className="text-xs text-slate-400 mt-3">
                      {[l.region, l.country].filter(Boolean).join(' · ')}
                    </p>
                  )}
                  {l.website && (
                    <a
                      href={l.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-900 hover:text-cyan-700 mt-4"
                      data-testid={`directory-website-${l.id}`}
                    >
                      Visit website <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Disclaimer */}
          <div className="mt-16 border-t border-slate-200 pt-8">
            <p className="text-xs text-slate-500 leading-relaxed max-w-4xl" data-testid="directory-disclaimer">
              {DISCLAIMER}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DirectoryPage;
